import { create } from 'zustand';

import { apiClient } from '@/services/api/client';
import { analyticsAllowed, useConsentStore } from '@/services/consent';
import { identifyUser, resetUser } from '@/services/monitoring/analytics';

import { appPath, djangoLoginUrl } from '../services/loginRedirect';
import { clearTokens } from '../services/token';

import type { AuthState, User, FlagResponse } from '../types';

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  flags: null,
  isAuthenticated: false,
  loading: true,

  checkAuth: async () => {
    try {
      const [userData, flagsData] = await Promise.all([
        apiClient.get<User>('/api/whoami/'),
        apiClient.get<FlagResponse>('/api/flag/'),
      ]);
      NABAT_DEBUG && console.info(flagsData);
      // If we have user data with username, user is authenticated
      if (userData && userData.username) {
        // Check if user has new_platform flag enabled
        if (!flagsData?.results.some((item: { code: string }) => item.code === 'new_platform')) {
          // Redirect to Angular app if new_platform flag is not enabled
          const angularAppUrl = import.meta.env.PROD
            ? 'https://app.nabat.ai/ngapp'
            : 'http://localhost:4200';
          window.location.href = angularAppUrl;
          return;
        }

        // The account's choice decides. An API without the field counts as on, the default.
        const analyticsEnabled = userData.analytics_enabled ?? analyticsAllowed();
        useConsentStore.getState().setAnalytics(analyticsEnabled ? 'granted' : 'denied');
        identifyUser(userData);

        set({
          user: userData,
          flags: flagsData.results,
          isAuthenticated: true,
          loading: false,
        });
      } else {
        throw new Error('No user data');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Auth check failed:', errorMessage);

      // If whoami fails, user is not authenticated
      set({
        user: null,
        flags: null,
        isAuthenticated: false,
        loading: false,
      });
    }
  },

  logout: async () => {
    resetUser();
    clearTokens();

    // End the Django session before leaving, or the next sign-in finds the user still signed in
    try {
      await apiClient.get('/api/signout/');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Sign out failed:', errorMessage);
    }

    // Clear client-side state. `loading` keeps ProtectedRoute from starting its own redirect.
    set({
      user: null,
      flags: null,
      isAuthenticated: false,
      loading: true,
    });

    window.location.href = import.meta.env.PROD ? djangoLoginUrl() : appPath('/login');
  },
}));
